<script setup lang="ts">
/** AR-E-RORC — Notice of Controllers (S.386AH/386AI). 등록가능지배자 1인당 1부(상위 DocScope).
 *  Yes/No 회신(is_still_controller · has_relevant_change)은 선택 시 문서에서 (Yes)/(No)로 동그라미 표시. */
import { inject } from 'vue';
import CddVarField from '../../aura/agm/cdd/CddVarField.vue';
import AgmSigners from '../../aura/agm/cdd/AgmSigners.vue';
import { useAgmFill, DOC_SCOPE } from '../../aura/agm/cdd/useCddFill';
import '../../aura/agm/docs/agmDoc.css';

const fill = useAgmFill();
const scope = inject(DOC_SCOPE, '');
function yn(k: string) { const v = String(fill.values[scope + k] ?? '').trim().toLowerCase(); return v === 'yes' ? 'yes' : v === 'no' ? 'no' : ''; }
function setYn(k: string, v: 'yes' | 'no') { fill.setValue(scope + k, yn(k) === v ? '' : v); }
</script>

<template>
  <div class="agm-doc">
    <div class="doc-head" style="text-align:center">
      <div class="doc-company"><CddVarField field-key="company_name" label="Company Name" /></div>
      <div>(Incorporated in the Republic of Singapore)</div>
      <div>Unique Entity Number: <CddVarField field-key="company_uen" label="UEN" /></div>
      <div>(“the Company”)</div>
    </div>
    <!-- 원본 Word(AR-E-RORC) 그대로: 제목 3줄(가운데·밑줄), 첫 문단 전문, 안내 2줄(빨간 글씨·기울임) -->
    <div class="rorc-title">
      <div class="t1">NOTICE OF CONTROLLERS</div>
      <div class="t2">FORM OF NOTICE MENTIONED IN SECTION 386AH(1) AND 386AI(1) OF THE SINGAPORE COMPANIES ACT 1967</div>
      <div class="t2">TO BE COMPLETED BY EXISTING CONTROLLER</div>
    </div>

    <p class="doc-p">Date of notice: <CddVarField field-key="notice_date" label="Notice date" /></p>
    <p class="doc-p">Attention: <CddVarField field-key="controller_name" label="Controller name" /></p>
    <p class="doc-p">This notice is sent under section 386AH(1) and 386AI(1) of the Singapore Companies Act 1967 and requires you to provide the following information <b><u>within 30 days</u></b> after the date of this notice. Please send your reply to <b><CddVarField field-key="reply_address" label="Reply address" /></b>.</p>
    <p class="doc-p notice-red">Please kindly complete and furnish the information as required below.</p>
    <p class="doc-p"><i>Failure to provide the information required by this notice may be an offence.</i></p>
    <p class="doc-p">Yours sincerely,<br />Name: <CddVarField field-key="secretary_name" label="Secretary name" /><br />Designation: <CddVarField field-key="secretary_designation" label="Designation" /></p>

    <p class="sec-title">1. Are you still a registrable controller of the Company?</p>
    <p class="doc-p">Your reply:
      <span class="circ" :class="{ sel: yn('is_still_controller') === 'yes' }" @click="setYn('is_still_controller','yes')">Yes</span>
      /
      <span class="circ" :class="{ sel: yn('is_still_controller') === 'no' }" @click="setYn('is_still_controller','no')">No</span>
      <span class="yn-hint">(circle your reply)</span>. If No, date you ceased to be a registrable controller: <CddVarField field-key="cessation_date" label="Cessation date" />.</p>

    <p class="sec-title">2. Particulars per your last declaration made on <CddVarField field-key="last_declaration_date" label="Last declaration date" /></p>
    <table class="cdd-tbl reg-tbl">
      <tbody>
        <tr><td class="reg-l">(i) Full name</td><td><CddVarField field-key="controller_full_name" label="Full name" /></td></tr>
        <tr><td class="reg-l">(ii) Aliases, if any</td><td><CddVarField field-key="controller_aliases" label="Aliases" /></td></tr>
        <tr><td class="reg-l">(iii) Residential address</td><td><CddVarField field-key="controller_residential_address" label="Residential address" /></td></tr>
        <tr><td class="reg-l">(iv) Nationality</td><td><CddVarField field-key="controller_nationality" label="Nationality" /></td></tr>
        <tr><td class="reg-l">(v) Identity card / passport number</td><td><CddVarField field-key="controller_id_no" label="NRIC/Passport" /></td></tr>
        <tr><td class="reg-l">(vi) Date of birth</td><td><CddVarField field-key="controller_date_of_birth" label="DOB" /></td></tr>
        <tr><td class="reg-l">(vii) Email address</td><td><CddVarField field-key="controller_email" label="Email" /></td></tr>
        <tr><td class="reg-l">(viii) Mobile phone number</td><td><CddVarField field-key="controller_mobile" label="Mobile" /></td></tr>
        <tr><td class="reg-l">(ix) Date you became a controller</td><td><CddVarField field-key="controller_became_date" label="Became date" /></td></tr>
      </tbody>
    </table>

    <p class="doc-p">Has a relevant change in your particulars set out above occurred? Your reply:
      <span class="circ" :class="{ sel: yn('has_relevant_change') === 'yes' }" @click="setYn('has_relevant_change','yes')">Yes</span>
      /
      <span class="circ" :class="{ sel: yn('has_relevant_change') === 'no' }" @click="setYn('has_relevant_change','no')">No</span>
      <span class="yn-hint">(circle your reply)</span></p>

    <p class="sec-title">3. If your reply is Yes, provide the following</p>
    <table class="cdd-tbl reg-tbl">
      <tbody>
        <tr><td class="reg-l">(a) Date of the change</td><td><CddVarField field-key="change_date" label="Change date" /></td></tr>
        <tr><td class="reg-l">(i) New full name</td><td><CddVarField field-key="new_full_name" label="New full name" /></td></tr>
        <tr><td class="reg-l">(ii) Aliases, if any</td><td><CddVarField field-key="new_aliases" label="Aliases" /></td></tr>
        <tr><td class="reg-l">(iii) New residential address</td><td><CddVarField field-key="new_residential_address" label="New residential address" /></td></tr>
        <tr><td class="reg-l">(iv) New nationality</td><td><CddVarField field-key="new_nationality" label="New nationality" /></td></tr>
        <tr><td class="reg-l">(v) New identity card / passport number</td><td><CddVarField field-key="new_id_no" label="New NRIC/Passport" /></td></tr>
        <tr><td class="reg-l">(vi) New email address</td><td><CddVarField field-key="new_email" label="New email" /></td></tr>
        <tr><td class="reg-l">(vii) New mobile phone number</td><td><CddVarField field-key="new_mobile" label="New mobile" /></td></tr>
      </tbody>
    </table>

    <p class="doc-p"><b>CONFIRMATION OF PARTICULARS BY REGISTERED CONTROLLER</b> — I/We confirm the particulars provided are true and correct, and will update the Company on any changes.</p>

    <div class="sign-block" style="margin-top:12px">
      <table class="cdd-tbl" style="width:auto">
        <tbody>
          <tr><td class="cdd-lcell">Name</td><td>:&nbsp; <CddVarField field-key="controller_name" label="Controller name" /></td></tr>
          <tr><td class="cdd-lcell">Designation</td><td>:&nbsp; <CddVarField field-key="controller_designation" label="Director / Shareholder" /></td></tr>
          <tr><td class="cdd-lcell">Shareholding</td><td>:&nbsp; <CddVarField field-key="controller_shareholding" label="%" /> %</td></tr>
          <tr><td class="cdd-lcell">Date</td><td>:&nbsp; <CddVarField field-key="declaration_date" label="Date" /></td></tr>
        </tbody>
      </table>
      <div style="margin-top:8px"><AgmSigners var-key="rorc_Signers" board-label="Controller" /></div>
    </div>
  </div>
</template>

<style scoped>
.rorc-title { text-align: center; margin: 10px 0 14px; }
.rorc-title .t1 { font-weight: 700; text-decoration: underline; }
.rorc-title .t2 { font-size: 11px; font-weight: 700; text-decoration: underline; margin-top: 4px; }
.notice-red { color: #d3382f; }
.doc-company { font-weight: 800; font-size: 13.5px; }
.circ { cursor: pointer; user-select: none; padding: 1px 9px; margin: 0 2px; display: inline-block; line-height: 1.5; }
.circ.sel { border: 1.6px solid #111; border-radius: 999px; font-weight: 700; }
.circ:hover { color: #1b64da; }
.yn-hint { font-size: 11px; color: #8a97b5; margin-left: 4px; }
.reg-tbl { width: 100%; border-collapse: collapse; margin: 4px 0; }
.reg-tbl td { border: 1px solid #d3dbe6; padding: 5px 8px; vertical-align: top; }
.reg-l { width: 46%; background: #f7f9fc; font-weight: 600; color: #42506a; }
</style>
